import Grid from '@mui/material/Grid';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { PageSection } from '@openx/components-core/lib/PageSection/PageSection.tsx';
import { AdFormat } from '@openx/types/gqlTypes.ts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetBidderCreativeQuery } from '../../../../graphql/query/creatives/GetBidderCreative.generated.tsx';
import { CreativeThumbnail } from '../../components/preview/CreativeThumbnail.tsx';
import { VideoThumbnail } from '../../components/preview/VideoThumbnail.tsx';
import { getMarkupTypeLabels } from '../../utils.ts';

type CreativeContentSectionProps = {
  creative: NonNullable<GetBidderCreativeQuery['bidderCreativeById']>;
};

export const CreativeContentSection = ({ creative }: CreativeContentSectionProps) => {
  const { t } = useTranslation();
  const [detectedDimensions, setDetectedDimensions] = useState<{ width: number; height: number } | null>(null);

  const handleDimensionsDetected = useCallback((dimensions: { width: number; height: number }) => {
    setDetectedDimensions(dimensions);
  }, []);

  const markupTypeLabels = useMemo(() => getMarkupTypeLabels(t), [t]);

  const isBanner = creative.ad_format === AdFormat.Banner;

  return (
    <PageSection divider title={t('Creative Content')}>
      <Grid
        size={5}
        sx={{
          gap: 1,
        }}
      >
        <FieldDetails title={t('Format Type')} value={markupTypeLabels[creative.markup_type]} />

        <FieldDetails
          title={t('Markup Payload / HTML / VAST / Source')}
          value={creative.markup_payload}
          textarea
          rows={6}
          copy
        />

        {isBanner && (
          <FieldDetails
            title={t('Size')}
            value={creative.width && creative.height ? `${creative.width}x${creative.height}` : undefined}
          />
        )}
      </Grid>

      <Grid size={1} />

      <Grid size={3}>
        {isBanner ? (
          <CreativeThumbnail
            html={creative.markup_payload}
            width={detectedDimensions?.width}
            height={detectedDimensions?.height}
            onDimensionsDetected={handleDimensionsDetected}
          />
        ) : (
          <VideoThumbnail markupPayload={creative.markup_payload} />
        )}
      </Grid>
    </PageSection>
  );
};
